export const getSectionFullCode = (code, survey) => {
  if (!code || !survey?.year || !survey?.type || !survey?.subType) {
    return code;
  }

  const codeParts = code.split('#');
  if (codeParts.length === 4) {
    return code;
  }

  return `${code}#${survey.year}#${survey.type.toUpperCase()}#${survey.subType.toUpperCase()}`;
};

export const prepareSectionMetadata = (metadata, oldMetadata, survey) => {
  if (survey?.type && survey?.subType && survey?.year) {
    const overrideCode = `${
      survey.year
    }#${survey.type.toUpperCase()}#${survey.subType.toUpperCase()}`;
    const newSectionMetadata = {
      ...oldMetadata,
      overrides: {
        ...oldMetadata?.overrides,
        [overrideCode]: {
          ...oldMetadata?.overrides?.[overrideCode],
        },
      },
      name: metadata?.name,
      code: getSectionFullCode(metadata?.code, survey),
    };

    return newSectionMetadata;
  }

  return metadata;
};

// Resolves the stored section entry for a survey's section code, matching the
// same three ways getSectionNameByCodeAndSurvey does: the composite code
// sections carry once they have been saved ("001#2025#BUF#ASLP"), the legacy
// per-survey override, then the bare identifier ("001"). Callers need this to
// find the section's Id — matching only on the bare code makes an already
// migrated section look new, and saving it again would create a duplicate
// instead of updating it.
export const findSectionByCodeAndSurvey = (code, survey, sections = []) => {
  if (!code) return undefined;

  const sectionIdentifier = String(code);
  const hasSurveyInfo = survey && survey.year && survey.type && survey.subType;
  const overrideCode = hasSurveyInfo
    ? `${survey.year}#${String(survey.type).toUpperCase()}#${String(survey.subType).toUpperCase()}`
    : null;
  const fullSectionCode = overrideCode ? `${sectionIdentifier}#${overrideCode}` : null;

  const findByCode = (candidateCode) =>
    sections.find(
      (s) =>
        s?.Data?.code && candidateCode && s.Data.code.toLowerCase() === candidateCode.toLowerCase()
    );

  const fullCodeSection = fullSectionCode ? findByCode(fullSectionCode) : undefined;
  if (fullCodeSection) return fullCodeSection;

  const overrideSection = overrideCode
    ? sections.find(
        (s) =>
          s?.Data?.overrides?.[overrideCode]?.code?.toLowerCase() ===
          sectionIdentifier.toLowerCase()
      )
    : undefined;
  if (overrideSection) return overrideSection;

  return findByCode(sectionIdentifier);
};

export const getSectionNameByCodeAndSurvey = (code, survey, sections = []) => {
  if (!code) return '';

  const sectionIdentifier = String(code);

  const hasSurveyInfo = survey && survey.year && survey.type && survey.subType;

  // "2025#BUF#ASLP"
  const overrideCode = hasSurveyInfo
    ? `${survey.year}#${String(survey.type).toUpperCase()}#${String(survey.subType).toUpperCase()}`
    : null;

  // Build full section code: "001#2025#BUF#ASLP"
  const fullSectionCode = overrideCode ? `${sectionIdentifier}#${overrideCode}` : null;

  const findByCode = (candidateCode) =>
    sections.find(
      (s) =>
        s?.Data?.code && candidateCode && s.Data.code.toLowerCase() === candidateCode.toLowerCase()
    );

  // Sections saved since BEN-2344 carry the full composite code.
  const fullCodeSection = fullSectionCode ? findByCode(fullSectionCode) : null;
  if (fullCodeSection) {
    return fullCodeSection.Data.name || sectionIdentifier;
  }

  // Sections saved before BEN-2344 keep their per-survey name under Data.overrides,
  // and are only migrated to a composite code once they are re-saved.
  const overrideSection = overrideCode
    ? sections.find(
        (s) =>
          s?.Data?.overrides?.[overrideCode]?.code?.toLowerCase() ===
          sectionIdentifier.toLowerCase()
      )
    : null;
  if (overrideSection) {
    return overrideSection.Data.overrides[overrideCode].name || sectionIdentifier;
  }

  // Otherwise match the simple identifier (e.g. "001"), then fall back to it.
  const section = findByCode(sectionIdentifier);
  return section?.Data?.name || sectionIdentifier;
};

// Every section code a survey has: the ones its questions use (summary
// sectionCodes) plus sections created for it in the wizard that have no
// questions yet, which only exist as metadata entries carrying the survey's
// composite suffix ("004#2025#BUF#ASLP"). Codes are returned bare and
// de-duplicated case-insensitively, keeping the first spelling seen.
export const getSurveySectionCodes = (survey, sections = []) => {
  const codes = (survey?.surveySummaryDetailsDto?.sectionCodes || '')
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);

  if (survey?.year && survey?.type && survey?.subType) {
    const overrideSuffix = `#${survey.year}#${String(survey.type).toUpperCase()}#${String(
      survey.subType
    ).toUpperCase()}`.toLowerCase();
    (sections || []).forEach((s) => {
      const fullCode = s?.Data?.code;
      if (typeof fullCode === 'string' && fullCode.toLowerCase().endsWith(overrideSuffix)) {
        codes.push(fullCode.slice(0, fullCode.length - overrideSuffix.length).trim());
      }
    });
  }

  const seen = new Set();
  return codes.filter((code) => {
    const key = code.toLowerCase();
    if (!code || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Next free section code for a survey: one past the highest numeric code it
// already uses, zero-padded to three digits ("001", "002", ...), skipping any
// code already taken (compared case-insensitively).
export const generateSectionCode = (survey, sections = []) => {
  const existing = getSurveySectionCodes(survey, sections);
  const taken = new Set(existing.map((code) => code.toLowerCase()));
  const highest = existing
    .filter((code) => /^\d+$/.test(code))
    .reduce((max, code) => Math.max(max, Number(code)), 0);

  let next = highest + 1;
  while (taken.has(String(next).padStart(3, '0'))) next += 1;
  return String(next).padStart(3, '0');
};

// Validation message for a section code typed in the wizard, or '' when it is
// valid. takenCodes: the survey's other section codes. Codes end up inside
// composite keys ("001#2025#BUF#ASLP") and question codes ("2025_BUF_001_Q1"),
// so only letters and digits are allowed.
export const getSectionCodeError = (code, takenCodes = []) => {
  const trimmed = (code || '').trim();
  if (!trimmed) return 'Section code is required';
  if (!/^[A-Za-z0-9]+$/.test(trimmed)) return 'Use letters and numbers only';
  if (takenCodes.some((taken) => taken.toLowerCase() === trimmed.toLowerCase())) {
    return `Section code ${trimmed.toUpperCase()} is already used in this survey`;
  }
  return '';
};
